/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {Observable} from 'rxjs';
import {CustomerBaseContract} from './contracts/customer/customer-base.contract';
import {Cacheable} from '../../decorators/cacheable.decorator';
import {PeriodResultContract} from './contracts/period/period-result.contract';
import {PeriodQueryContract} from './contracts/period/period-query.contract';
import {PeriodDetailContract} from './contracts/period/period-detail.contract';
import {SettlementDetailContract} from './contracts/settlement/settlement-detail.contract';
import {BundleMetadataContract} from './contracts/bundle/bundle-metadata.contract';
import {BundleMetadataValidationContract} from './contracts/bundle/bundle-metadata-validation.contract';
import {BundleConfigContract} from './contracts/bundle-config/bundle-config.contract';
import {PeriodEndDateContract} from './contracts/period/period-end-date.contract';
import {BundlingSettingsDetailContract} from './contracts/bundle-settings/bundling-settings-detail.contract';
import {BundleTypeBaseContract} from './contracts/bundle-type/bundle-type-base.contract';
import {PeriodicityBaseContract} from './contracts/periodicity-base.contract';
import {BundleCriterionSpecBaseContract} from './contracts/bundle/bundle-criterion-spec-base.contract';
import {BundlePositionCriterionSpecBaseContract} from './contracts/bundle/bundle-position-criterion-spec-base.contract';
import {BundlingSettingsContract} from './contracts/bundle-settings/bundling-settings.contract';
import {PeriodTypeBaseContract} from './contracts/period-type-base.contract';
import {BundleStatusBaseContract} from './contracts/bundle/bundle-status-base.contract';
import {ReferenceDataContract} from './contracts/reference-data.contract';

@Injectable({
    providedIn: 'root'
})
export class BundlesApi {
    private baseUrl = `${environment.bundlesBaseUrl}/api`;
    private bundleBaseUrl = `${this.baseUrl}/bundle`;
    private periodBaseUrl = `${this.baseUrl}/period`;
    private settingsBundles = `${this.baseUrl}/settings`;
    private suppliers = `${this.baseUrl}/supplier`;

    private httpOptions = {
        headers: new HttpHeaders({
            'accept': 'application/json',
            'Content-Type': 'application/json'
        })
    };

    constructor(private http: HttpClient) {
    }

    /**
     * Gets bundles configuration
     */
    @Cacheable()
    getBundlesConfig(): Observable<BundleConfigContract> {
        return this.http.get<BundleConfigContract>(`${this.baseUrl}/config`);
    }

    /**
     * Gets all Customers detail
     */
    @Cacheable()
    getAllCustomersDetail(): Observable<CustomerBaseContract[]> {
        return this.http.get<CustomerBaseContract[]>(`${this.baseUrl}/customer/base`);
    }

    /**
     * Gets all bundle types
     */
    @Cacheable()
    getAllBundleTypes(): Observable<BundleTypeBaseContract[]> {
        return this.http.get<BundleTypeBaseContract[]>(`${this.baseUrl}/bundleType/base`);
    }

    /**
     * Gets Periods from the BE
     * @param periodsQuery the search query criteria
     */
    getPeriods(periodsQuery: PeriodQueryContract): Observable<PeriodResultContract> {
        return this.http.post<PeriodResultContract>(`${this.periodBaseUrl}/search/detail`, periodsQuery, this.httpOptions);
    }

    /**
     * This method retrieves the period detail
     * @param periodCode the period code
     */
    getPeriodDetail(periodCode: string): Observable<PeriodDetailContract> {
        return this.http.get<PeriodDetailContract>(`${this.periodBaseUrl}/detail/${periodCode}`);
    }

    /**
     * This method update the end date on a period list element
     * @param periodCode the period code
     * @param endDate the new end date
     */
    updateEndDate(periodCode: string, endDate: PeriodEndDateContract): Observable<void> {
        return this.http.put<void>(`${this.periodBaseUrl}/${periodCode}/updateEndDate`, endDate, this.httpOptions);
    }

    /**
     * This method retrieves the settlement detail
     * @param complaintCode the complaint code
     * @param settlementCode the settlement code
     */
    getSettlementDetail(complaintCode: string, settlementCode: string): Observable<SettlementDetailContract> {
        return this.http.get<SettlementDetailContract>(`${this.baseUrl}/settlement/detail/${complaintCode}/${settlementCode}`,
                this.httpOptions);
    }

    /**
     * This method manually closes a bundle
     * @param bundleNumber the number of the bundle to close
     * @param bundleMetadata the metadata of the bundle to close
     */
    closeBundle(bundleNumber: string, bundleMetadata: BundleMetadataContract): Observable<void> {
        return this.http.post<void>(`${this.bundleBaseUrl}/${bundleNumber}/close`, bundleMetadata);
    }

    /**
     * This method validates if a bundle is valid to be closed
     * @param bundleNumber the number of the bundle to close
     * @param bundleMetadata the metadata of the bundle to close
     */
    validateBundle(bundleNumber: string, bundleMetadata: BundleMetadataContract): Observable<BundleMetadataValidationContract> {
        return this.http.post<BundleMetadataValidationContract>(`${this.bundleBaseUrl}/${bundleNumber}/validate`, bundleMetadata);
    }

    /**
     * This method marks a bundle as SENT
     * @param bundleNumber the bundle number
     */
    markBundleAsSent(bundleNumber: string): Observable<void> {
        return this.http.put<void>(`${this.bundleBaseUrl}/${bundleNumber}/sent`, this.httpOptions);
    }

    /**
     * This method marks a bundle as UNSENT
     * @param bundleNumber the bundle number
     */
    markBundleAsUnsent(bundleNumber: string): Observable<void> {
        return this.http.put<void>(`${this.bundleBaseUrl}/${bundleNumber}/unsent`, this.httpOptions);
    }

    /**
     * This method get the settings for a bundle type and customer
     * @param customer the bundle number
     * @param bundleType the bundle number
     */
    getBundlesSettings(customer: string, bundleType: string): Observable<BundlingSettingsDetailContract> {
        return this.http.get<BundlingSettingsDetailContract>(
                `${this.settingsBundles}/customer/${customer}/bundleType/${bundleType}/detail/latest`,
                this.httpOptions);
    }

    /**
     * This method get all the periodicities active
     */
    @Cacheable()
    getActivePeriodicitiesBaseInfo(): Observable<PeriodicityBaseContract[]> {
        return this.http.get<PeriodicityBaseContract[]>(`${this.baseUrl}/periodicity/base/active`);
    }

    /**
     * This method get all the bundle criterion specs active
     */
    @Cacheable()
    getActiveBundleCriterionSpecs(): Observable<BundleCriterionSpecBaseContract[]> {
        return this.http.get<BundleCriterionSpecBaseContract[]>(`${this.baseUrl}/bundleCriterionSpec/base/active`);
    }

    /**
     * This method get all the bundle position criterion specs active
     */
    @Cacheable()
    getActiveBundlePositionCriterionSpecs(): Observable<BundlePositionCriterionSpecBaseContract[]> {
        return this.http.get<BundlePositionCriterionSpecBaseContract[]>(`${this.baseUrl}/bundlePositionCriterionSpec/base/active`);
    }

    @Cacheable()
    getAllPeriodTypes(): Observable<PeriodTypeBaseContract[]> {
        return this.http.get<PeriodTypeBaseContract[]>(`${this.baseUrl}/periodType/base`);
    }

    @Cacheable()
    getAllBundleStatuses(): Observable<BundleStatusBaseContract[]> {
        return this.http.get<BundleStatusBaseContract[]>(`${this.baseUrl}/bundleStatus/base`);
    }

    @Cacheable()
    /**
     * This method retrieves all suppliers.json by code/name
     * @param codeOrName the code or name
     * @param firstResult The first result
     * @param maxResults The max result
     */
    getAllSuppliers(codeOrName: string,
                    firstResult: number,
                    maxResults: number): Observable<ReferenceDataContract[]> {
        return this.http.get<ReferenceDataContract[]>(`${this.suppliers}/base?` + [
            `codeOrName=${codeOrName}`,
            `firstResult=${firstResult}`,
            `maxResults=${maxResults}`
        ].join(('&')));
    }

    /**
     * This method post the new bundle settings
     * @param customer the bundle number
     * @param bundleType the bundle number
     * @param bundlingSettings the new bundle settings created
     */
    createBundlingSettings(customer: string, bundleType: string, bundlingSettings: BundlingSettingsContract): Observable<void> {
        return this.http.post<void>(
                `${this.settingsBundles}/customer/${customer}/bundleType/${bundleType}`, bundlingSettings);
    }

    /**
     * This method update a bundle settings to status disabled
     * @param customer the bundle number
     * @param bundleType the bundle number
     */
    disableBundlingSettings(customer: string, bundleType: string): Observable<void> {
        return this.http.put<void>(
                `${this.settingsBundles}/customer/${customer}/bundleType/${bundleType}/disable`, this.httpOptions);
    }
}
